import { forwardRef, type ReactNode, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAppliedInferenceSet, selectClient, selectSessionAdvisor } from '../model';
import styles from './OnboardingPdfReportV2.module.css';
import { FileText, BarChart3, TrendingUp, ShieldCheck, Banknote, Coins } from 'lucide-react';
import {
    careEnvironmentDefs,
    carePhaseDefs,
    formatCurrency,
    formatLtcCostDisplay,
    formatDurationYears,
    formatInteger,
    formatPercent,
    formatThousands,
    fundingPolicyTypeDefs,
    getLtcCostRoundingStep,
    isCarePhasePast,
    FundingPolicyType,
    isNullOrUndefined,
    isDurationCustom,
    checkPolicyCalculationTypeEquals,
    CarePhase,
    calcFutureValue,
    calcCapitalGainsTaxLiability,
    calcFutureValueOfMonthlyContributions,
} from '@shared';
import { cn } from '@/lib/utils';
import { isInferenceEdited } from '../util';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PRODUCTS_BY_POLICY_TYPE } from '@client/lib/nyc-lif-demo';
import { useGuidedPlanningSession } from '@client/features/guided-financial-planning';
import {
    AgedWomanIllustration,
    ManEarlyAssistantIllustration,
    ManLateAssistantIllustration,
    MiddleAgeManIllustration,
    MiddleAgeWomanIllustration,
    usePdfReportSections,
    WomanEarlyAssistantIllustration,
    WomanLateAssistantIllustration,
    YoungBoyIllustration,
    YoungGirlIllustration,
} from '@/features/client-onboarding';
import { possessivePronoun, pronoun } from '@/util';

import CompanyLogoContainer from '../components/CompanyLogo';

// Helper to calculate widths for the progress bar
function calculatePhaseWidths(phases: { cost: number }[]) {
    const total = phases.reduce((acc, curr) => acc + curr.cost, 0);
    if (total === 0) return phases.map(() => 0);
    return phases.map(p => (p.cost / total) * 100);
}

function getSelfFundingName(name?: string | null) {
    const defaultName = 'Self-funding';
    return !name || name === defaultName ? defaultName : `${defaultName} (${name})`;
}

function getRoiValue(
    source: SelfFundingSource | AnnuitySource | PolicyFundingSource | FundingSources,
    displayType?: PdfRoiDisplayType,
): number | undefined {
    const type = displayType || 'totalROI';

    if ('selfFundingProjectedReturnOnInvestment' in source) {
        const s = source as SelfFundingSource;
        return type === 'IRR'
            ? s.selfFundingProjectedInternalRateOfReturn
            : type === 'CAGR'
                ? s.selfFundingProjectedCompoundAnnualGrowthRate
                : s.selfFundingProjectedReturnOnInvestment;
    }

    if ('annuityProjectedReturnOnInvestment' in source) {
        const a = source as AnnuitySource;
        return type === 'IRR'
            ? a.annuityProjectedInternalRateOfReturn
            : type === 'CAGR'
                ? a.annuityProjectedCompoundAnnualGrowthRate
                : a.annuityProjectedReturnOnInvestment;
    }

    if ('policyProjectedReturnOnInvestment' in source) {
        const p = source as PolicyFundingSource;
        return type === 'IRR'
            ? p.policyProjectedInternalRateOfReturn
            : type === 'CAGR'
                ? p.policyProjectedCompoundAnnualGrowthRate
                : p.policyProjectedReturnOnInvestment;
    }

    const f = source as FundingSources;
    return type === 'IRR'
        ? f.combinedProjectedInternalRateOfReturn
        : type === 'CAGR'
            ? f.combinedProjectedCompoundAnnualGrowthRate
            : f.combinedProjectedReturnOnInvestment;
}

export const OnboardingPdfReportV2 = forwardRef<HTMLDivElement>((props, ref) => {
    const client = useSelector(selectClient)!;
    const sessionAdvisor = useSelector(selectSessionAdvisor);
    const { flags } = useFeatureFlags();
    const onboardingPdfReportOptionsEnabled = flags.onboardingPdfReportOptions === true;
    const { includeCareJourney, includePredictedCareNeeds, hydrate } = usePdfReportSections();
    const { ltcLikelihoodEver, ltcAtAge } = useSelector(selectAppliedInferenceSet);

    useLayoutEffect(() => {
        if (onboardingPdfReportOptionsEnabled) {
            hydrate();
        }
    }, [hydrate, onboardingPdfReportOptionsEnabled]);

    const {
        allPhaseCosts: {
            allPhaseCareHoursProvided,
            allPhaseFamilyCareHoursProvided,
            allPhaseFamilyProvidedPercent,
            allPhaseProfessionalCareHoursProvided,
            allPhaseInflatedProfessionalShareCost,
            allPhaseCareMonthsNeeded,
        },
        fundingSources: {
            annuity: {
                hasAnnuity,
                annuityAnnualPayOut,
                annuityAnnualPayoutForLtc,
                annuityAnnualPayoutForLtcPeriodYears,
                annuityExpectedPaymentEndAge,
                annuityExpectedPaymentStartAge,
                annuityPurchasePrice,
                annuityProjectedReturnOnInvestment,
                annuityTotalValue,
                annuityProjectedCostCoverage,
                annuityNonLtcPayout,
                isDeactivated: annuityIsDeactivated,
            },
            combinedFundingTotalCost,
            combinedProjectedCostCoverage,
            combinedProjectedReturnOnInvestment,
            combinedTotalValue,
            combinedProjectedCostCoveragePercent,
            combinedNonLtcPayout,
        },
        intakeSurvey: { clientHasStartedLtc },
        multipleFundingSources,
        phaseCosts,
    } = client;

    const roundingStep = flags.ltcCostRoundingOptions
        ? getLtcCostRoundingStep(client?.clientAdvisorSettings?.ltcCostDisplayPrecision)
        : 0;
    const formatLtcCost = (value: number) =>
        formatLtcCostDisplay(value, { roundingStep }).formattedDisplay;
    const pdfLtcDisplayRoundingStep =
        flags.ltcCostRoundingOptions && roundingStep > 0 ? roundingStep : null;

    // Discount for CAGR comes from client calculation settings. fetchClientByClientId merges advisor
    // defaultDiscountRateForCagr into discountRate when the client has no saved row — no PDF fallback.
    const effectiveDiscountRateForCagr = client.clientCalculationSettings?.discountRate ?? undefined;

    const clientRoiCalculationType =
        client.clientCalculationSettings?.returnOnInvestmentCalculationType;
    const hideReturnOnInvestmentInPdf =
        client.clientCalculationSettings?.hideReturnOnInvestmentInPdf === true;

    const clientRoiToPdfDisplayType: Record<ReturnOnInvestmentCalculationType, PdfRoiDisplayType> = {
        total: 'totalROI',
        internalRateOfReturn: 'IRR',
        compoundAnnualGrowthRate: 'CAGR',
    };

    const clientDrivenPdfType: PdfRoiDisplayType | undefined = (() => {
        if (hideReturnOnInvestmentInPdf) {
            return 'hidden';
        }
        if (clientRoiCalculationType === undefined || clientRoiCalculationType === null) {
            return undefined;
        }
        const mapped = clientRoiToPdfDisplayType[clientRoiCalculationType];
        if (
            mapped === 'CAGR' &&
            (effectiveDiscountRateForCagr === undefined || effectiveDiscountRateForCagr === null)
        ) {
            return 'totalROI';
        }
        return mapped;
    })();

    // PDF ROI display defaults to totalROI when returnOnInvestmentCalculationType is unset. We do not
    // use advisor pdfRoiDisplayType (PROD: almost all such clients matched totalROI; a few had advisor
    // "hidden" — accepted). Server-side discount merge for clients without a discount row remains.
    const effectiveRoiDisplayType: PdfRoiDisplayType = clientDrivenPdfType ?? 'totalROI';
    const cagrDiscountRateLabel =
        effectiveDiscountRateForCagr !== undefined && effectiveDiscountRateForCagr !== null
            ? ` (${Number(effectiveDiscountRateForCagr) === Math.round(Number(effectiveDiscountRateForCagr)) ? Math.round(Number(effectiveDiscountRateForCagr)) : Number(effectiveDiscountRateForCagr).toFixed(1)}% discount rate)`
            : '';

    const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
    const anyCustomDurations = isDurationCustom(phaseCosts);

    // Filter Active Policies
    const ltcPolicies = Object.values(multipleFundingSources.ltcPolicy).filter(
        ltcPolicy => !ltcPolicy.isDeactivated,
    );

    const { session: guidedPlanningSession } = useGuidedPlanningSession(client.clientId);

    // Filter Active Self Funding keeping IDs for guided planning allocations
    const selfFundingSources = Object.entries(multipleFundingSources.selfFunding)
        .filter(([, selfFunding]) => {
            if (selfFunding.isDeactivated) return false;

            const areSelfFundingFieldsPopulated =
                (selfFunding.annualRateOfReturn && selfFunding.annualRateOfReturn > 0) ||
                (selfFunding.monthlyContributionAmount && selfFunding.monthlyContributionAmount > 0) ||
                (selfFunding.existingAssetsValue && selfFunding.existingAssetsValue > 0);

            return selfFunding.hasSelfFunding || areSelfFundingFieldsPopulated;
        })
        .map(([id, data]) => ({ id, data }));

    const guidedFundingAllocations =
        guidedPlanningSession?.stepData?.planComparisonSnapshot?.fundingAllocations ?? [];
    const allocationBySourceId = guidedFundingAllocations.reduce(
        (acc, allocation) => {
            acc[allocation.selfFundingSourceId] = allocation;
            return acc;
        },
        {} as Record<string, { amount: number; existingValue?: number }>,
    );

    // Extract costs for the phases for the progress bar
    const phase1 = phaseCosts.find(p => p.carePhase === CarePhase.earlyCare);
    const phase2 = phaseCosts.find(p => p.carePhase === CarePhase.moderateCare);
    const phase3 = phaseCosts.find(p => p.carePhase === CarePhase.fullCare);

    const phaseOneCost = phase1?.phaseInflatedProfessionalShareCost ?? 0;
    const phaseTwoCost = phase2?.phaseInflatedProfessionalShareCost ?? 0;
    const phaseThreeCost = phase3?.phaseInflatedProfessionalShareCost ?? 0;

    const phaseWidths = calculatePhaseWidths([
        { cost: phaseOneCost },
        { cost: phaseTwoCost },
        { cost: phaseThreeCost },
    ]);

    const allFundingSources = [
        ...selfFundingSources.map(s => ({ type: 'self-funding' as const, data: s.data, id: s.id })),
        ...(hasAnnuity && !annuityIsDeactivated
            ? [{ type: 'annuity' as const, data: client.fundingSources.annuity }]
            : []),
        ...ltcPolicies.map(p => ({ type: 'policy' as const, data: p })),
    ];

    const sourceChunks: (typeof allFundingSources)[] = [];
    for (let i = 0; i < allFundingSources.length; i += 2) {
        sourceChunks.push(allFundingSources.slice(i, i + 2));
    }

    return (
        <div
            ref={ref}
            className={styles.PdfContainer}
            id="client-pdf-report"
        >
            <table className={styles.printTable}>
                <thead>
                    <tr>
                        <td>
                            <div className={styles.pageHeaderSpacer} />
                        </td>
                    </tr>
                </thead>
                <tfoot>
                    <tr>
                        <td>
                            <div className={styles.pageFooterSpacer} />
                            <FundingDisclosure
                                clientFirstName={client.clientFirstName}
                                ltcDisplayRoundingStep={pdfLtcDisplayRoundingStep}
                            />
                            <div className={styles.pageFooterSafetySpacer} />
                        </td>
                    </tr>
                </tfoot>
                <tbody>
                    <tr>
                        <td>
                            {/* HEADER & PREDICTIONS CARD */}
                            <div className={styles.headerCard}>
                                {/* WHITE LABEL & HEADER */}
                                <div className={styles.headerContainer}>
                                    <div>
                                        {/* White Label Integration */}
                                        <CompanyLogoContainer
                                            organizationDisplayName={sessionAdvisor.organizationDisplayName}
                                            containerClassName={styles.headerLogoContainer}
                                            organizationNameClassName={styles.headerOrgName}
                                        />
                                        <div className={styles.reportTitle}>
                                            <FileText size={16} />
                                            Long-Term Care Financial Report
                                        </div>
                                        <h1 className={styles.clientName}>
                                            {client.clientFirstName} {client.clientLastName}
                                        </h1>
                                        <p className={styles.reportDescription}>
                                            Welcome to your long-term care plan report. This document provides a
                                            comprehensive overview of your current financial care plan for you and your
                                            family.
                                        </p>
                                    </div>
                                    <div className={styles.reportDate}>
                                        <div className={styles.reportDateLabel}>Report Date</div>
                                        <div className={styles.reportDateValue}>
                                            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* PREDICTIONS */}
                            <div className={cn(styles.predictionsGrid, 'grid grid-cols-2 gap-4 md:grid-cols-4')}>
                                {!clientHasStartedLtc && (
                                    <div
                                        className={cn(
                                            styles.predictionCard,
                                            styles.cardPurple,
                                            'rounded-xl border p-5 text-center',
                                        )}
                                    >
                                        <div
                                            className={cn(
                                                styles.predictionLabel,
                                                'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                                            )}
                                        >
                                            CARE LIKELIHOOD
                                        </div>
                                        <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                                            {formatPercent(ltcLikelihoodEver, 0)}
                                        </div>
                                        <div
                                            className={cn(
                                                styles.predictionSubtitle,
                                                'text-muted-foreground mt-1 text-xs',
                                            )}
                                        >
                                            National Avg: 56%
                                        </div>
                                    </div>
                                )}
                                {!clientHasStartedLtc && (
                                    <div
                                        className={cn(
                                            styles.predictionCard,
                                            styles.cardCyan,
                                            'rounded-xl border p-5 text-center',
                                        )}
                                    >
                                        <div
                                            className={cn(
                                                styles.predictionLabel,
                                                'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                                            )}
                                        >
                                            START AGE
                                        </div>
                                        <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                                            {ltcAtAge} yrs
                                        </div>
                                        {isInferenceEdited(client, 'ltcAtAge') && (
                                            <div
                                                className={cn(
                                                    styles.predictionSubtitle,
                                                    'text-muted-foreground mt-1 text-xs',
                                                )}
                                            >
                                                (edited)
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div
                                    className={cn(
                                        styles.predictionCard,
                                        styles.cardLightGreen,
                                        'rounded-xl border p-5 text-center',
                                    )}
                                >
                                    <div
                                        className={cn(
                                            styles.predictionLabel,
                                            'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                                        )}
                                    >
                                        {clientHasStartedLtc ? 'REMAINING CARE' : 'CARE DURATION'}
                                    </div>
                                    <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                                        {formatDurationYears(ltcDurationYears)} yrs
                                    </div>
                                    {anyCustomDurations && (
                                        <div
                                            className={cn(
                                                styles.predictionSubtitle,
                                                'text-muted-foreground mt-1 text-xs',
                                            )}
                                        >
                                            (edited)
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={cn(
                                        styles.predictionCard,
                                        styles.cardGray,
                                        'rounded-xl border p-5 text-center',
                                    )}
                                >
                                    <div
                                        className={cn(
                                            styles.predictionLabel,
                                            'text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider',
                                        )}
                                    >
                                        TOTAL HOURS
                                    </div>
                                    <div className={cn(styles.predictionValue, 'text-primary text-2xl font-bold')}>
                                        {formatThousands(allPhaseCareHoursProvided)}
                                    </div>
                                </div>
                            </div>

                            {/* PROJECTED CARE COSTS */}
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>
                                    <BarChart3 size={20} />
                                </div>
                                <div className={styles.sectionTitle}>Projected Care Costs</div>
                            </div>

                            <div className={styles.costsContainer}>
                                <div className={styles.totalCostRow}>
                                    <div className={styles.totalCostLabel}>
                                        {client.clientFirstName}'s {clientHasStartedLtc ? 'remaining' : 'projected'}{' '}
                                        costs
                                    </div>
                                    <div className={styles.totalCostValue}>
                                        {formatLtcCost(allPhaseInflatedProfessionalShareCost)}
                                    </div>
                                </div>

                                <div className={styles.phasesGrid}>
                                    {phase1 && (
                                        <div className={cn(styles.phaseCard, styles.phaseCardHome)}>
                                            <div className={styles.phaseCardHeader}>
                                                <div
                                                    className={cn(
                                                        styles.phaseLabel,
                                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                    )}
                                                >
                                                    {carePhaseDefs[phase1.carePhase].label.toUpperCase()}
                                                </div>
                                                <div
                                                    className={cn(styles.phaseValue, 'text-primary mb-1 text-2xl font-bold')}
                                                >
                                                    {formatLtcCost(phaseOneCost)}
                                                </div>
                                                <div className={cn(styles.phaseSub, 'text-muted-foreground text-xs')}>
                                                    {isCarePhasePast(phase1.carePhase, client)
                                                        ? 'Past'
                                                        : careEnvironmentDefs[phase1.appliedCareEnvironment].label}
                                                </div>
                                            </div>
                                            <div className={styles.phaseDetails}>
                                                {onboardingPdfReportOptionsEnabled ? (
                                                    <>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Duration:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {Math.round(phase1.phaseCareMonthsNeeded)} months (
                                                                {formatDurationYears(phase1.phaseCareMonthsNeeded / 12)} yrs)
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Total Cost Today:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phase1.phaseProfessionalShareCost ?? 0)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Total Future Cost:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phaseOneCost)}
                                                            </span>
                                                        </div>
                                                        {phase1.phaseCareMonthsNeeded > 0 && (
                                                            <div className={cn(styles.phaseDetailItem)}>
                                                                <span className={styles.phaseDetailLabel}>Annual Cost:</span>
                                                                <span className={styles.phaseDetailValue}>
                                                                    {formatLtcCost(
                                                                        phaseOneCost / (phase1.phaseCareMonthsNeeded / 12),
                                                                    )}
                                                                    /yr
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Hours:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatThousands(phase1.phaseCareHoursProvided ?? 0)}
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Cost Today:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phase1.phaseProfessionalShareCost ?? 0)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Future Cost:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phaseOneCost)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Hours:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatThousands(phase1.phaseCareHoursProvided ?? 0)}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {phase2 && (
                                        <div className={cn(styles.phaseCard, styles.phaseCardMod)}>
                                            <div className={styles.phaseCardHeader}>
                                                <div
                                                    className={cn(
                                                        styles.phaseLabel,
                                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                    )}
                                                >
                                                    {carePhaseDefs[phase2.carePhase].label.toUpperCase()}
                                                </div>
                                                <div
                                                    className={cn(styles.phaseValue, 'text-primary mb-1 text-2xl font-bold')}
                                                >
                                                    {formatLtcCost(phaseTwoCost)}
                                                </div>
                                                <div className={cn(styles.phaseSub, 'text-muted-foreground text-xs')}>
                                                    {isCarePhasePast(phase2.carePhase, client)
                                                        ? 'Past'
                                                        : careEnvironmentDefs[phase2.appliedCareEnvironment].label}
                                                </div>
                                            </div>
                                            <div className={styles.phaseDetails}>
                                                {onboardingPdfReportOptionsEnabled ? (
                                                    <>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Duration:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {Math.round(phase2.phaseCareMonthsNeeded)} months (
                                                                {formatDurationYears(phase2.phaseCareMonthsNeeded / 12)} yrs)
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Total Cost Today:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phase2.phaseProfessionalShareCost ?? 0)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Total Future Cost:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phaseTwoCost)}
                                                            </span>
                                                        </div>
                                                        {phase2.phaseCareMonthsNeeded > 0 && (
                                                            <div className={cn(styles.phaseDetailItem)}>
                                                                <span className={styles.phaseDetailLabel}>Annual Cost:</span>
                                                                <span className={styles.phaseDetailValue}>
                                                                    {formatLtcCost(
                                                                        phaseTwoCost / (phase2.phaseCareMonthsNeeded / 12),
                                                                    )}
                                                                    /yr
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Hours:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatThousands(phase2.phaseCareHoursProvided ?? 0)}
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Cost Today:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phase2.phaseProfessionalShareCost ?? 0)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Future Cost:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phaseTwoCost)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Hours:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatThousands(phase2.phaseCareHoursProvided ?? 0)}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {phase3 && (
                                        <div className={cn(styles.phaseCard, styles.phaseCardFull)}>
                                            <div className={styles.phaseCardHeader}>
                                                <div
                                                    className={cn(
                                                        styles.phaseLabel,
                                                        'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                    )}
                                                >
                                                    {carePhaseDefs[phase3.carePhase].label.toUpperCase()}
                                                </div>
                                                <div
                                                    className={cn(styles.phaseValue, 'text-primary mb-1 text-2xl font-bold')}
                                                >
                                                    {formatLtcCost(phaseThreeCost)}
                                                </div>
                                                <div className={cn(styles.phaseSub, 'text-muted-foreground text-xs')}>
                                                    {isCarePhasePast(phase3.carePhase, client)
                                                        ? 'Past'
                                                        : careEnvironmentDefs[phase3.appliedCareEnvironment].label}
                                                </div>
                                            </div>
                                            <div className={styles.phaseDetails}>
                                                {onboardingPdfReportOptionsEnabled ? (
                                                    <>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Duration:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {Math.round(phase3.phaseCareMonthsNeeded)} months (
                                                                {formatDurationYears(phase3.phaseCareMonthsNeeded / 12)} yrs)
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Total Cost Today:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phase3.phaseProfessionalShareCost ?? 0)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Total Future Cost:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phaseThreeCost)}
                                                            </span>
                                                        </div>
                                                        {phase3.phaseCareMonthsNeeded > 0 && (
                                                            <div className={cn(styles.phaseDetailItem)}>
                                                                <span className={styles.phaseDetailLabel}>Annual Cost:</span>
                                                                <span className={styles.phaseDetailValue}>
                                                                    {formatLtcCost(
                                                                        phaseThreeCost / (phase3.phaseCareMonthsNeeded / 12),
                                                                    )}
                                                                    /yr
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Hours:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatThousands(phase3.phaseCareHoursProvided ?? 0)}
                                                            </span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Cost Today:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phase3.phaseProfessionalShareCost ?? 0)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Future Cost:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatLtcCost(phaseThreeCost)}
                                                            </span>
                                                        </div>
                                                        <div className={cn(styles.phaseDetailItem)}>
                                                            <span className={styles.phaseDetailLabel}>Hours:</span>
                                                            <span className={styles.phaseDetailValue}>
                                                                {formatThousands(phase3.phaseCareHoursProvided ?? 0)}
                                                            </span>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className={styles.progressBarContainer}>
                                    <div className={styles.progressBar}>
                                        <div
                                            style={{ width: `${phaseWidths[0]}%` }}
                                            className={cn(styles.progressSegmentEarly, 'transition-all')}
                                        />
                                        <div
                                            style={{ width: `${phaseWidths[1]}%` }}
                                            className={cn(styles.progressSegmentModerate, 'transition-all')}
                                        />
                                        <div
                                            style={{ width: `${phaseWidths[2]}%` }}
                                            className={cn(styles.progressSegmentFull, 'transition-all')}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* CARE HOURS DISTRIBUTION */}
                            <div className={styles.careHoursContainer}>
                                <h3 className={styles.careHoursTitle}>
                                    {clientHasStartedLtc ? 'Remaining Care Hours' : 'Total Care Hours'} Distribution
                                </h3>
                                <div className={styles.hoursDistribution}>
                                    <div>
                                        <div className={styles.hoursRow}>
                                            <div className={styles.hoursLabel}>Professional Care Services</div>
                                            <div className={styles.hoursValue}>
                                                {formatThousands(allPhaseProfessionalCareHoursProvided)} hrs
                                            </div>
                                        </div>
                                        <div className={styles.hoursBarBg}>
                                            <div
                                                className={cn(styles.hoursBarFill, styles.barPurple)}
                                                style={{
                                                    width: `${(allPhaseProfessionalCareHoursProvided / allPhaseCareHoursProvided) * 100}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className={styles.hoursRow}>
                                            <div className={styles.hoursLabel}>Family Provided Care</div>
                                            <div className={styles.hoursValue}>
                                                {formatThousands(allPhaseFamilyCareHoursProvided)} hrs
                                            </div>
                                        </div>
                                        <div className={styles.hoursBarBg}>
                                            <div
                                                className={cn(styles.hoursBarFill, styles.barPurple)}
                                                style={{
                                                    width: `${(allPhaseFamilyCareHoursProvided / allPhaseCareHoursProvided) * 100}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {onboardingPdfReportOptionsEnabled && (
                                    <table className={styles.hoursPhaseTable}>
                                        <thead>
                                            <tr>
                                                <th className={styles.hoursPhaseTableHeader}>Phase</th>
                                                <th className={styles.hoursPhaseTableHeader}>Professional</th>
                                                <th className={styles.hoursPhaseTableHeader}>Family</th>
                                                <th className={styles.hoursPhaseTableHeader}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {phase1 && (
                                                <tr>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {carePhaseDefs[phase1.carePhase].label}
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase1.phaseProfessionalCareHoursProvided)} hrs
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase1.phaseFamilyCareHoursProvided)} hrs
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase1.phaseCareHoursProvided)} hrs
                                                    </td>
                                                </tr>
                                            )}
                                            {phase2 && (
                                                <tr>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {carePhaseDefs[phase2.carePhase].label}
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase2.phaseProfessionalCareHoursProvided)} hrs
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase2.phaseFamilyCareHoursProvided)} hrs
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase2.phaseCareHoursProvided)} hrs
                                                    </td>
                                                </tr>
                                            )}
                                            {phase3 && (
                                                <tr>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {carePhaseDefs[phase3.carePhase].label}
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase3.phaseProfessionalCareHoursProvided)} hrs
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase3.phaseFamilyCareHoursProvided)} hrs
                                                    </td>
                                                    <td className={styles.hoursPhaseTableCell}>
                                                        {formatThousands(phase3.phaseCareHoursProvided)} hrs
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {onboardingPdfReportOptionsEnabled && includeCareJourney && <PdfCareJourneySection />}
                            {onboardingPdfReportOptionsEnabled && includePredictedCareNeeds && (
                                <PdfPredictedCareNeedsSection />
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Funding Strategy on new page (outside table so print break rules apply) */}
            <div className={cn('pt-2', styles.pageBreakBefore, styles.fundingStrategySection)}>
                <table className={styles.printTable}>
                    <thead>
                        <tr>
                            <td>
                                <div className={styles.pageHeaderSpacer} />
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}>
                                        <TrendingUp size={20} />
                                    </div>
                                    <div className={styles.sectionTitle}>Funding Strategy</div>
                                </div>

                                <div className="flex flex-col gap-4">
                                    {sourceChunks.map((chunk, chunkIdx) => (
                                        <div
                                            key={`chunk-${chunkIdx}`}
                                            className={styles.fundingGrid}
                                        >
                                            {chunk.map((source, idx) => {
                                                if (source.type === 'self-funding') {
                                                    const s = source.data;
                                                    const allocation = allocationBySourceId[source.id];
                                                    const originalExistingAssetsValue =
                                                        guidedPlanningSession?.stepData?.multipleFundingSourcesSnapshot
                                                            ?.selfFunding?.[source.id]?.existingAssetsValue ??
                                                        allocation?.existingValue ??
                                                        s.existingAssetsValue;
                                                    const assetsUsedForPolicy = allocation?.amount;
                                                    const remainingPrincipal = Math.max(
                                                        0,
                                                        (originalExistingAssetsValue ?? 0) - (assetsUsedForPolicy ?? 0),
                                                    );
                                                    const futureValueOfRemainingPrincipal = isNullOrUndefined(
                                                        s.annualRateOfReturn,
                                                    )
                                                        ? 0
                                                        : calcFutureValue(
                                                            remainingPrincipal,
                                                            s.annualRateOfReturn ?? 0,
                                                            client.appliedInferenceSet.yearsTillLtc,
                                                        );
                                                    const netFutureValueOfRemainingPrincipal =
                                                        futureValueOfRemainingPrincipal -
                                                        calcCapitalGainsTaxLiability(
                                                            remainingPrincipal,
                                                            s.gainsTaxRate,
                                                            futureValueOfRemainingPrincipal,
                                                        );
                                                    const futureValueOfMonthlyContributions = isNullOrUndefined(
                                                        s.monthlyContributionAmount,
                                                    )
                                                        ? 0
                                                        : calcFutureValueOfMonthlyContributions(
                                                            s.monthlyContributionAmount ?? 0,
                                                            s.annualRateOfReturn ?? 0,
                                                            s.contributionMonthCount ?? 0,
                                                        );
                                                    const netFutureValueOfMonthlyContributions =
                                                        futureValueOfMonthlyContributions -
                                                        calcCapitalGainsTaxLiability(
                                                            (s.monthlyContributionAmount ?? 0) * (s.contributionMonthCount ?? 0),
                                                            s.gainsTaxRate,
                                                            futureValueOfMonthlyContributions,
                                                        );
                                                    const remainingProjectedLtcPayout =
                                                        netFutureValueOfRemainingPrincipal +
                                                        netFutureValueOfMonthlyContributions +
                                                        (s.monthlyIncomeTotal ?? 0);
                                                    return (
                                                        <div
                                                            key={`self-funding-${idx}`}
                                                            className={styles.fundingCard}
                                                        >
                                                            <div className={styles.fundingHeaderSection}>
                                                                <div className={styles.fundingCardHeader}>
                                                                    <div className={styles.sectionIcon}>
                                                                        <TrendingUp size={16} />
                                                                    </div>
                                                                    <h3 className={styles.fundingCardTitle}>
                                                                        {getSelfFundingName(s.selfFundingName)}
                                                                    </h3>
                                                                </div>

                                                                <div
                                                                    className={cn(
                                                                        styles.fundingHeaderGrid,
                                                                        styles.fundingHeaderGridThree,
                                                                    )}
                                                                >
                                                                    <div className={styles.fundingHeaderItem}>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderLabel,
                                                                                'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                                            )}
                                                                        >
                                                                            Initial Assets
                                                                            {onboardingPdfReportOptionsEnabled && <sup>1</sup>}
                                                                        </div>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderValue,
                                                                                styles.primaryValue,
                                                                                'text-2xl font-bold',
                                                                            )}
                                                                        >
                                                                            {formatCurrency(originalExistingAssetsValue ?? 0)}
                                                                        </div>
                                                                    </div>
                                                                    <div className={styles.fundingHeaderItem}>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderLabel,
                                                                                'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                                            )}
                                                                        >
                                                                            Assets Used to Fund LTC Policy
                                                                        </div>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderValue,
                                                                                'text-2xl font-bold',
                                                                            )}
                                                                        >
                                                                            {isNullOrUndefined(assetsUsedForPolicy)
                                                                                ? '-'
                                                                                : formatLtcCost(assetsUsedForPolicy)}
                                                                        </div>
                                                                    </div>
                                                                    <div className={styles.fundingHeaderItem}>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderLabel,
                                                                                'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                                            )}
                                                                        >
                                                                            Projected Remaining LTC Payout
                                                                            {onboardingPdfReportOptionsEnabled && <sup>2</sup>}
                                                                        </div>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderValue,
                                                                                styles.primaryValue,
                                                                                'text-2xl font-bold',
                                                                            )}
                                                                        >
                                                                            {formatLtcCost(remainingProjectedLtcPayout ?? 0)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className={styles.fundingRowsContainer}>
                                                                <FundingRow
                                                                    label={
                                                                        onboardingPdfReportOptionsEnabled ? (
                                                                            <>
                                                                                Today&rsquo;s Assets for LTC<sup>1</sup>
                                                                            </>
                                                                        ) : (
                                                                            "Today's Assets for LTC"
                                                                        )
                                                                    }
                                                                    value={
                                                                        isNullOrUndefined(s.existingAssetsValue)
                                                                            ? '-'
                                                                            : formatCurrency(s.existingAssetsValue)
                                                                    }
                                                                />
                                                                {!clientHasStartedLtc && (
                                                                    <>
                                                                        <FundingRow
                                                                            label="Monthly Contribution"
                                                                            value={
                                                                                isNullOrUndefined(s.monthlyContributionAmount)
                                                                                    ? '-'
                                                                                    : `${formatCurrency(s.monthlyContributionAmount)} over ${s.contributionYearCount} year${s.contributionYearCount === 1 ? '' : 's'}`
                                                                            }
                                                                        />
                                                                        <FundingRow
                                                                            label="ARR | Gains Tax"
                                                                            value={`${isNullOrUndefined(s.annualRateOfReturn) ? '-' : formatPercent(s.annualRateOfReturn, 2)} | ${isNullOrUndefined(s.gainsTaxRate) ? '-' : formatPercent(s.gainsTaxRate, 2)}`}
                                                                        />
                                                                    </>
                                                                )}
                                                                <FundingRow
                                                                    label={
                                                                        onboardingPdfReportOptionsEnabled ? (
                                                                            <>
                                                                                Total Monthly Income<sup>3</sup>
                                                                            </>
                                                                        ) : (
                                                                            'Total Monthly Income'
                                                                        )
                                                                    }
                                                                    value={
                                                                        isNullOrUndefined(s.monthlyIncomeTotal)
                                                                            ? '-'
                                                                            : formatCurrency(s.monthlyIncomeTotal)
                                                                    }
                                                                />
                                                                {!clientHasStartedLtc && (
                                                                    <>
                                                                        <FundingRow
                                                                            label={
                                                                                onboardingPdfReportOptionsEnabled ? (
                                                                                    <>
                                                                                        Total Principal Invested over {s.contributionYearCount}{' '}
                                                                                        year{s.contributionYearCount === 1 ? '' : 's'}
                                                                                        <sup>4</sup>
                                                                                    </>
                                                                                ) : (
                                                                                    `Total Principal Invested over ${s.contributionYearCount} year${s.contributionYearCount === 1 ? '' : 's'}`
                                                                                )
                                                                            }
                                                                            value={formatCurrency(s.selfFundingTotalCost)}
                                                                            className={styles.textRed}
                                                                        />
                                                                        <FundingRow
                                                                            label={
                                                                                onboardingPdfReportOptionsEnabled ? (
                                                                                    <>
                                                                                        LTC Payout<sup>5</sup>
                                                                                    </>
                                                                                ) : (
                                                                                    'LTC Payout'
                                                                                )
                                                                            }
                                                                            value={formatLtcCost(remainingProjectedLtcPayout)}
                                                                            className={styles.textGreenPositive}
                                                                        />
                                                                        <FundingRow
                                                                            label="Non-LTC Payout"
                                                                            value={formatLtcCost(s.selfFundingNonLtcPayout ?? 0)}
                                                                            className={styles.textGreenPositive}
                                                                        />
                                                                        <FundingRow
                                                                            label="Total Payout"
                                                                            value={formatLtcCost(s.selfFundingTotalValue ?? 0)}
                                                                            className={styles.textGreenPositive}
                                                                        />
                                                                        {effectiveRoiDisplayType !== 'hidden' && (
                                                                            <FundingRow
                                                                                label={
                                                                                    effectiveRoiDisplayType === 'IRR'
                                                                                        ? 'IRR (Internal Rate of Return) for Modeled Scenario'
                                                                                        : effectiveRoiDisplayType === 'CAGR'
                                                                                            ? `CAGR${cagrDiscountRateLabel} for Modeled Scenario`
                                                                                            : 'Return on Investment for Modeled Scenario'
                                                                                }
                                                                                value={formatPercent(
                                                                                    getRoiValue(s, effectiveRoiDisplayType),
                                                                                    2,
                                                                                )}
                                                                                className={styles.textGreenPositive}
                                                                            />
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                            {!isNullOrUndefined(assetsUsedForPolicy) &&
                                                                assetsUsedForPolicy > 0 && (
                                                                    <div className={styles.fundingFootnote}>
                                                                        Part of your initial assets (
                                                                        {formatCurrency(assetsUsedForPolicy)}) was used to fund the LTC
                                                                        policy. The amounts above show the remainder from this source
                                                                        available for LTC, after growth and taxes.
                                                                    </div>
                                                                )}
                                                            {onboardingPdfReportOptionsEnabled && (
                                                                <div className={styles.fundingFootnote}>
                                                                    <div>
                                                                        <sup>1</sup> Initial Assets and Today&rsquo;s Assets for LTC are
                                                                        expressed in today&rsquo;s dollars.
                                                                    </div>
                                                                    <div>
                                                                        <sup>2</sup> Projected Remaining LTC Payout is expressed in
                                                                        future dollars after tax.
                                                                    </div>
                                                                    <div>
                                                                        <sup>3</sup> Total Monthly Income over{' '}
                                                                        {formatDurationYears(ltcDurationYears)} years of long-term care,
                                                                        expressed in future dollars.
                                                                    </div>
                                                                    {!clientHasStartedLtc && (
                                                                        <>
                                                                            <div>
                                                                                <sup>4</sup> Total Principal Invested is the sum of
                                                                                Today&rsquo;s Assets for LTC
                                                                                {!isNullOrUndefined(s.monthlyContributionAmount) &&
                                                                                    s.monthlyContributionAmount > 0
                                                                                    ? ` plus monthly contributions over ${s.contributionYearCount} year${s.contributionYearCount === 1 ? '' : 's'}`
                                                                                    : ''}
                                                                                {!isNullOrUndefined(s.monthlyIncomeTotal) &&
                                                                                    s.monthlyIncomeTotal > 0
                                                                                    ? ', plus Total Monthly Income'
                                                                                    : ''}
                                                                                .
                                                                            </div>
                                                                            <div>
                                                                                <sup>5</sup> LTC Payout is the projected amount available
                                                                                for LTC in future dollars, after tax on investment gains,
                                                                                including future monthly income.
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                if (source.type === 'annuity') {
                                                    const a = source.data;
                                                    return (
                                                        <div
                                                            key="annuity"
                                                            className={styles.fundingCard}
                                                        >
                                                            <div className={styles.fundingHeaderSection}>
                                                                <div className={styles.fundingCardHeader}>
                                                                    <div className={styles.sectionIcon}>
                                                                        <Banknote size={16} />
                                                                    </div>
                                                                    <h3 className={styles.fundingCardTitle}>Annuity</h3>
                                                                </div>

                                                                <div className={styles.fundingHeaderGrid}>
                                                                    <div className={styles.fundingHeaderItem}>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderLabel,
                                                                                'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                                            )}
                                                                        >
                                                                            Annual Payout
                                                                        </div>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderValue,
                                                                                'text-2xl font-bold',
                                                                            )}
                                                                        >
                                                                            {formatCurrency(a.annuityAnnualPayOut)}
                                                                        </div>
                                                                    </div>
                                                                    <div className={styles.fundingHeaderItem}>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderLabel,
                                                                                'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                                            )}
                                                                        >
                                                                            Projected LTC Payout
                                                                        </div>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderValue,
                                                                                styles.primaryValue,
                                                                                'text-2xl font-bold',
                                                                            )}
                                                                        >
                                                                            {formatLtcCost(a.annuityProjectedCostCoverage ?? 0)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className={styles.fundingRowsContainer}>
                                                                <FundingRow
                                                                    label="LTC Annual Payout"
                                                                    value={
                                                                        isNullOrUndefined(a.annuityAnnualPayoutForLtc)
                                                                            ? '-'
                                                                            : formatCurrency(a.annuityAnnualPayoutForLtc)
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="LTC Payout Period"
                                                                    value={
                                                                        isNullOrUndefined(a.annuityAnnualPayoutForLtcPeriodYears)
                                                                            ? '-'
                                                                            : `${formatInteger(a.annuityAnnualPayoutForLtcPeriodYears)} years`
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="Expected Payout Duration"
                                                                    value={`Ages ${formatInteger(a.annuityExpectedPaymentStartAge)}-${formatInteger(a.annuityExpectedPaymentEndAge)}`}
                                                                />
                                                                <FundingRow
                                                                    label="Purchase Price"
                                                                    value={formatCurrency(a.annuityPurchasePrice)}
                                                                    className={styles.textRed}
                                                                />
                                                                <FundingRow
                                                                    label="LTC Coverage"
                                                                    value={formatLtcCost(a.annuityProjectedCostCoverage ?? 0)}
                                                                    className={styles.textGreenPositive}
                                                                />
                                                                <FundingRow
                                                                    label="Non-LTC Payout"
                                                                    value={formatLtcCost(a.annuityNonLtcPayout ?? 0)}
                                                                    className={styles.textGreenPositive}
                                                                />
                                                                <FundingRow
                                                                    label="Total Payout"
                                                                    value={formatLtcCost(a.annuityTotalValue ?? 0)}
                                                                    className={styles.textGreenPositive}
                                                                />
                                                                {effectiveRoiDisplayType !== 'hidden' && (
                                                                    <FundingRow
                                                                        label={
                                                                            effectiveRoiDisplayType === 'IRR'
                                                                                ? 'IRR (Internal Rate of Return) for Modeled Scenario'
                                                                                : effectiveRoiDisplayType === 'CAGR'
                                                                                    ? `CAGR${cagrDiscountRateLabel} for Modeled Scenario`
                                                                                    : 'Return on Investment for Modeled Scenario'
                                                                        }
                                                                        value={formatPercent(
                                                                            getRoiValue(a, effectiveRoiDisplayType),
                                                                            2,
                                                                        )}
                                                                        className={styles.textGreenPositive}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                if (source.type === 'policy') {
                                                    const p = source.data;
                                                    return (
                                                        <div
                                                            key={`policy-${idx}`}
                                                            className={styles.fundingCard}
                                                        >
                                                            <div className={styles.fundingHeaderSection}>
                                                                <div className={styles.fundingCardHeader}>
                                                                    <div className={styles.sectionIcon}>
                                                                        <ShieldCheck size={16} />
                                                                    </div>
                                                                    <h3 className={styles.fundingCardTitle}>
                                                                        {p.policyType
                                                                            ? flags.nycLifeDemo
                                                                                ? PRODUCTS_BY_POLICY_TYPE[p.policyType] ||
                                                                                fundingPolicyTypeDefs[p.policyType].label
                                                                                : fundingPolicyTypeDefs[p.policyType].label
                                                                            : 'LTC Insurance Policy'}
                                                                    </h3>
                                                                </div>

                                                                <div className={styles.fundingHeaderGrid}>
                                                                    <div className={styles.fundingHeaderItem}>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderLabel,
                                                                                'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                                            )}
                                                                        >
                                                                            {clientHasStartedLtc
                                                                                ? 'Remaining Benefit'
                                                                                : checkPolicyCalculationTypeEquals(p.policyType, [
                                                                                    FundingPolicyType.hybridLifeInsurance,
                                                                                    FundingPolicyType.lifeInsuranceWithRider,
                                                                                ])
                                                                                    ? 'Death Benefit'
                                                                                    : 'Max Benefit'}
                                                                        </div>
                                                                        <div className={styles.fundingHeaderValue}>
                                                                            {isNullOrUndefined(p.policyMaximumBenefitAmount)
                                                                                ? 'Unlimited'
                                                                                : clientHasStartedLtc
                                                                                    ? formatCurrency(
                                                                                        p.policyMaximumBenefitAmount +
                                                                                        (p.policyContinuationBenefitAmount ?? 0) -
                                                                                        (p.policyBenefitUtilizedToDate ?? 0),
                                                                                    )
                                                                                    : checkPolicyCalculationTypeEquals(p.policyType, [
                                                                                        FundingPolicyType.hybridLifeInsurance,
                                                                                        FundingPolicyType.lifeInsuranceWithRider,
                                                                                    ])
                                                                                        ? formatLtcCost(p.policyMaximumBenefitAmount)
                                                                                        : formatCurrency(p.policyMaximumBenefitAmount)}
                                                                        </div>
                                                                    </div>
                                                                    <div className={styles.fundingHeaderItem}>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderLabel,
                                                                                'text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wider',
                                                                            )}
                                                                        >
                                                                            Projected LTC Payout
                                                                        </div>
                                                                        <div
                                                                            className={cn(
                                                                                styles.fundingHeaderValue,
                                                                                styles.primaryValue,
                                                                                'text-2xl font-bold',
                                                                            )}
                                                                        >
                                                                            {(() => {
                                                                                const projectedLtcPayout = clientHasStartedLtc
                                                                                    ? p.policyCoverageOfRemainingLtcCosts
                                                                                    : p.projectedCostCoverage;
                                                                                if (isNullOrUndefined(projectedLtcPayout)) {
                                                                                    return '-';
                                                                                }
                                                                                return formatLtcCost(projectedLtcPayout);
                                                                            })()}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className={styles.fundingRowsContainer}>
                                                                {checkPolicyCalculationTypeEquals(
                                                                    p.policyType,
                                                                    FundingPolicyType.hybridLifeInsurance,
                                                                ) && (
                                                                        <FundingRow
                                                                            label="COB Amount"
                                                                            value={
                                                                                isNullOrUndefined(p.policyContinuationBenefitAmount)
                                                                                    ? 'Unlimited'
                                                                                    : formatCurrency(p.policyContinuationBenefitAmount)
                                                                            }
                                                                        />
                                                                    )}

                                                                {checkPolicyCalculationTypeEquals(p.policyType, [
                                                                    FundingPolicyType.hybridLifeInsurance,
                                                                    FundingPolicyType.lifeInsuranceWithRider,
                                                                ]) && (
                                                                        <FundingRow
                                                                            label="Guaranteed Death Benefit"
                                                                            value={formatCurrency(
                                                                                p.policyMinimumGuaranteedBenefitAmount ?? 0,
                                                                            )}
                                                                        />
                                                                    )}

                                                                <FundingRow
                                                                    label="Policy Purchase Year"
                                                                    value={
                                                                        isNullOrUndefined(p.policyPremiumStartYear)
                                                                            ? '-'
                                                                            : formatInteger(p.policyPremiumStartYear)
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="Home/Facility Waiting Period"
                                                                    value={`${formatThousands(p.homeCareWaitingPeriodDays ?? 0)}/${formatThousands(p.facilityCareWaitingPeriodDays ?? 0)} days`}
                                                                />
                                                                <FundingRow
                                                                    label="Monthly Premium"
                                                                    value={
                                                                        isNullOrUndefined(p.policyPremiumMonthlyCost)
                                                                            ? '-'
                                                                            : formatCurrency(p.policyPremiumMonthlyCost)
                                                                    }
                                                                />
                                                                {p.policyPremiumIncreaseRate && (
                                                                    <FundingRow
                                                                        label="Premium Increase Rate"
                                                                        value={formatPercent(p.policyPremiumIncreaseRate)}
                                                                    />
                                                                )}
                                                                {p.policyLimitedPayYears && (
                                                                    <FundingRow
                                                                        label="Limited Pay Period"
                                                                        value={`${formatThousands(p.policyLimitedPayYears)} years`}
                                                                    />
                                                                )}
                                                                <FundingRow
                                                                    label="Lump-Sum Payment"
                                                                    value={
                                                                        isNullOrUndefined(p.policyLumpSumPayment)
                                                                            ? '-'
                                                                            : formatThousands(p.policyLumpSumPayment)
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="Home Care Max*"
                                                                    value={
                                                                        isNullOrUndefined(p.dailyHomeBenefitAmount)
                                                                            ? '-'
                                                                            : `${formatCurrency(p.dailyHomeBenefitAmount)}/day`
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="Independent Living Max*"
                                                                    value={
                                                                        isNullOrUndefined(p.dailyIndependentLivingBenefitAmount)
                                                                            ? '-'
                                                                            : `${formatCurrency(p.dailyIndependentLivingBenefitAmount)}/day`
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="Assisted Living Max*"
                                                                    value={
                                                                        isNullOrUndefined(p.dailyAssistedLivingBenefitAmount)
                                                                            ? '-'
                                                                            : `${formatCurrency(p.dailyAssistedLivingBenefitAmount)}/day`
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="Nursing Home Max*"
                                                                    value={
                                                                        isNullOrUndefined(p.dailyNursingHomeBenefitAmount)
                                                                            ? '-'
                                                                            : `${formatCurrency(p.dailyNursingHomeBenefitAmount)}/day`
                                                                    }
                                                                />
                                                                <div className={styles.fundingFootnote}>
                                                                    * Daily maximum estimated by converting a monthly maximum to a
                                                                    daily value - dividing by 30. Some policies use a single maximum
                                                                    across care settings.
                                                                </div>
                                                                <FundingRow
                                                                    label="Inflation Protection"
                                                                    value={(() => {
                                                                        // Check if we have a general inflation protection value
                                                                        if (
                                                                            !isNullOrUndefined(p.policyInflationProtection) &&
                                                                            p.policyInflationProtection !== 0
                                                                        ) {
                                                                            return `${formatPercent(p.policyInflationProtection, 2)} (${p.isSimpleInflationProtection ? 'Simple' : 'Compound'})`;
                                                                        }

                                                                        // Check if we have separate AOB/COB inflation values
                                                                        const hasAOB =
                                                                            !isNullOrUndefined(p.policyInflationProtectionOnFaceAmount) &&
                                                                            p.policyInflationProtectionOnFaceAmount !== 0;
                                                                        const hasCOB =
                                                                            !isNullOrUndefined(p.policyInflationProtectionOnCOBAmount) &&
                                                                            p.policyInflationProtectionOnCOBAmount !== 0;

                                                                        if (hasAOB && hasCOB) {
                                                                            return `${formatPercent(p.policyInflationProtectionOnFaceAmount, 2)} AOB, ${formatPercent(p.policyInflationProtectionOnCOBAmount, 2)} COB`;
                                                                        } else if (hasAOB) {
                                                                            return `${formatPercent(p.policyInflationProtectionOnFaceAmount, 2)} AOB`;
                                                                        } else if (hasCOB) {
                                                                            return `${formatPercent(p.policyInflationProtectionOnCOBAmount, 2)} COB`;
                                                                        }

                                                                        return '-';
                                                                    })()}
                                                                />
                                                                {p.policyInflationProtectionYears && (
                                                                    <FundingRow
                                                                        label="Inflation Protection Duration"
                                                                        value={`${formatThousands(p.policyInflationProtectionYears)} years`}
                                                                    />
                                                                )}
                                                                <FundingRow
                                                                    label="Payment Type"
                                                                    value={
                                                                        isNullOrUndefined(p.isIndemnityPolicyPayment) ||
                                                                            p.isIndemnityPolicyPayment === false
                                                                            ? 'Reimbursement'
                                                                            : 'Indemnity'
                                                                    }
                                                                />
                                                                <FundingRow
                                                                    label="Premiums Paid"
                                                                    value={formatCurrency(p.policyTotalCost)}
                                                                    className={styles.textRed}
                                                                />
                                                                <FundingRow
                                                                    label="LTC Coverage"
                                                                    value={formatLtcCost(
                                                                        (clientHasStartedLtc
                                                                            ? p.policyCoverageOfRemainingLtcCosts
                                                                            : p.projectedCostCoverage) ?? 0,
                                                                    )}
                                                                    className={styles.textGreenPositive}
                                                                />
                                                                <FundingRow
                                                                    label="Non-LTC Payout"
                                                                    value={formatLtcCost(p.policyNonLtcPayout ?? 0)}
                                                                    className={styles.textGreenPositive}
                                                                />
                                                                <FundingRow
                                                                    label="Total Payout"
                                                                    value={formatLtcCost(p.policyTotalValue ?? 0)}
                                                                    className={styles.textGreenPositive}
                                                                />
                                                                {effectiveRoiDisplayType !== 'hidden' && (
                                                                    <FundingRow
                                                                        label={
                                                                            effectiveRoiDisplayType === 'IRR'
                                                                                ? 'IRR (Internal Rate of Return) for Modeled Scenario'
                                                                                : effectiveRoiDisplayType === 'CAGR'
                                                                                    ? `CAGR${cagrDiscountRateLabel} for Modeled Scenario`
                                                                                    : 'Return on Investment for Modeled Scenario'
                                                                        }
                                                                        value={formatPercent(
                                                                            getRoiValue(p, effectiveRoiDisplayType),
                                                                            2,
                                                                        )}
                                                                        className={styles.textGreenPositive}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                return null;
                                            })}
                                        </div>
                                    ))}
                                </div>

                                {/* COMBINED SUMMARY */}
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}>
                                        <Coins size={20} />
                                    </div>
                                    <div className={styles.sectionTitle}>Combined Funding Summary</div>
                                </div>

                                <div className={styles.summaryContainer}>
                                    <div
                                        className={cn(
                                            styles.summaryRow,
                                            effectiveRoiDisplayType === 'hidden' && styles.summaryRowNoRoi,
                                        )}
                                    >
                                        <div className={styles.summaryItem}>
                                            <div className={styles.summaryLabel}>TOTAL INVESTED*</div>
                                            <div className={styles.summaryValue}>
                                                {formatLtcCost(combinedFundingTotalCost ?? 0)}
                                            </div>
                                        </div>
                                        <div className={styles.summaryItem}>
                                            <div className={styles.summaryLabel}>LTC COVERAGE*</div>
                                            <div className={styles.summaryValue}>
                                                {formatLtcCost(combinedProjectedCostCoverage ?? 0)}
                                            </div>
                                        </div>
                                        <div className={styles.summaryItem}>
                                            <div className={styles.summaryLabel}>NON-LTC PAYOUT*</div>
                                            <div className={styles.summaryValue}>
                                                {formatLtcCost(combinedNonLtcPayout ?? 0)}
                                            </div>
                                        </div>
                                        <div className={styles.summaryItem}>
                                            <div className={styles.summaryLabel}>COVERAGE %*</div>
                                            <div className={styles.summaryValue}>
                                                {formatPercent(combinedProjectedCostCoveragePercent ?? 0, 0)}
                                            </div>
                                        </div>
                                        {effectiveRoiDisplayType !== 'hidden' && (
                                            <div className={cn(styles.summaryItem, styles.summaryItemDark)}>
                                                <div className={styles.summaryLabel}>
                                                    COMBINED{' '}
                                                    {effectiveRoiDisplayType === 'IRR'
                                                        ? 'IRR'
                                                        : effectiveRoiDisplayType === 'CAGR'
                                                            ? `CAGR${cagrDiscountRateLabel}`
                                                            : 'ROI'}
                                                    *
                                                </div>
                                                <div className={styles.summaryValue}>
                                                    {formatPercent(
                                                        getRoiValue(client.fundingSources, effectiveRoiDisplayType) ?? 0,
                                                        2,
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.summaryDisclaimer}>
                                        * The values are representative of the modeled scenario and are not guaranteed.
                                    </div>
                                </div>

                                {onboardingPdfReportOptionsEnabled && (
                                    <RoiAppendix roiDisplayType={effectiveRoiDisplayType} />
                                )}
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>
                                <div className={styles.pageFooterSpacer} />
                                <FundingDisclosure
                                    clientFirstName={client.clientFirstName}
                                    ltcDisplayRoundingStep={pdfLtcDisplayRoundingStep}
                                />
                                <div className={styles.pageFooterSafetySpacer} />
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
});

OnboardingPdfReportV2.displayName = 'OnboardingPdfReportV2';

function ltcRoundingDisclaimerWord(step: number): string | null {
    if (step === 10) return 'ten';
    if (step === 100) return 'hundred';
    if (step === 1000) return 'thousand';
    return null;
}

function PdfCareJourneySection() {
        const client = useSelector(selectClient)!;
        const { ltcLikelihoodEver, ltcAtAge } = useSelector(selectAppliedInferenceSet);
        const {
            clientFirstName,
            intakeSurvey,
            allPhaseCosts: { allPhaseCareMonthsNeeded, allPhaseCareHoursProvided },
            intakeSurvey: { clientHasStartedLtc },
            phaseCosts,
        } = client;

        const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
        const subjectPronoun = pronoun(intakeSurvey);
        const possessive = possessivePronoun(intakeSurvey);
        const isMale = subjectPronoun === 'he';
        const ltcDecade = `${Math.floor(ltcAtAge / 10) * 10}s`;
        const preDecade = `${Math.floor((ltcAtAge - 10) / 10) * 10}s`;
        const slowdownLabel =
            subjectPronoun === 'they'
                ? 'They are slowing down but still capable'
                : `${subjectPronoun === 'he' ? 'He' : 'She'} is slowing down but still capable`;
        const CurrentIllustration = isMale ? YoungBoyIllustration : YoungGirlIllustration;
        const MidIllustration = isMale ? MiddleAgeManIllustration : MiddleAgeWomanIllustration;

        return (
            <div className={styles.optionalSectionContainer}>
                <div className={styles.optionalSectionHeader}>
                    <TrendingUp size={16} />
                    {clientFirstName}&rsquo;s Care Journey
                </div>
                <div className={styles.optionalSectionBody}>
                    {!clientHasStartedLtc && (
                        <p className={styles.optionalNarrative}>
                            We predict, in {possessive} {ltcDecade}, {clientFirstName} will need help with daily
                            activities.
                        </p>
                    )}
                    <div className={styles.optionalJourneyGrid}>
                        <div className={styles.optionalIllustrationCard}>
                            <div className={styles.optionalIllustrationWrap}>
                                <CurrentIllustration className={styles.optionalIllustrationSvg} />
                            </div>
                            <div className={styles.optionalIllustrationTitle}>Current State</div>
                            <div className={styles.optionalIllustrationSubtitle}>Independent and fully capable</div>
                        </div>

                        <div className={styles.optionalIllustrationCard}>
                            <div className={styles.optionalIllustrationWrap}>
                                <MidIllustration className={styles.optionalIllustrationSvg} />
                            </div>
                            <div className={styles.optionalIllustrationTitle}>
                                By {possessive} {preDecade}
                            </div>
                            <div className={styles.optionalIllustrationSubtitle}>{slowdownLabel}</div>
                        </div>

                        <div className={cn(styles.optionalIllustrationCard, styles.optionalLtcCard)}>
                            <div className={styles.optionalIllustrationWrap}>
                                <AgedWomanIllustration className={styles.optionalIllustrationSvg} />
                            </div>
                            <div className={styles.optionalIllustrationTitle}>In need of LTC help</div>
                            <div className={styles.optionalIllustrationSubtitle}>
                                Unable to perform daily activities alone
                            </div>
                        </div>
                    </div>
                    <div className={styles.optionalSummaryStats}>
                        <div className={styles.optionalSummaryStat}>
                            <span className={styles.optionalSummaryStatLabel}>
                                {clientHasStartedLtc ? 'Remaining Care' : 'Care Duration'}
                            </span>
                            <span className={styles.optionalSummaryStatValue}>
                                {formatDurationYears(ltcDurationYears)} yrs
                            </span>
                            {isDurationCustom(phaseCosts) && (
                                <span className={styles.optionalSummaryStatUnit}>(edited)</span>
                            )}
                        </div>
                        <div className={styles.optionalSummaryStat}>
                            <span className={styles.optionalSummaryStatLabel}>Total Hours</span>
                            <span className={styles.optionalSummaryStatValue}>
                                {formatThousands(allPhaseCareHoursProvided)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
}

function PdfPredictedCareNeedsSection() {
        const {
            clientFirstName,
            intakeSurvey,
            phaseCosts,
            allPhaseCosts: { allPhaseCareMonthsNeeded },
        } = useSelector(selectClient)!;

        const weeksPerMonth = 4.33;
        const ltcDurationYears = allPhaseCareMonthsNeeded / 12;
        const isMale = pronoun(intakeSurvey) === 'he';

        const earlyPhaseIndex = carePhaseDefs[CarePhase.earlyCare].index;
        const earlyPhaseCosts = phaseCosts[earlyPhaseIndex];
        const initialHoursPerWeek =
            earlyPhaseCosts.phaseCareHoursNeeded / earlyPhaseCosts.phaseCareMonthsNeeded / weeksPerMonth;

        const latePhaseIndex = carePhaseDefs[CarePhase.fullCare].index;
        const latePhaseCosts = phaseCosts[latePhaseIndex];
        const laterHoursPerWeek =
            latePhaseCosts.phaseCareHoursNeeded / latePhaseCosts.phaseCareMonthsNeeded / weeksPerMonth;
        const EarlyIllustration = isMale
            ? ManEarlyAssistantIllustration
            : WomanEarlyAssistantIllustration;
        const LateIllustration = isMale ? ManLateAssistantIllustration : WomanLateAssistantIllustration;

        return (
            <div className={styles.optionalSectionContainer}>
                <div className={styles.optionalSectionHeader}>
                    <ShieldCheck size={16} />
                    {clientFirstName}&rsquo;s Predicted Care Needs
                </div>
                <div className={styles.optionalSectionBody}>
                    <p className={styles.optionalNarrative}>
                        Later stages may require more intensive support for daily activities like bathing,
                        dressing, and mobility.
                    </p>
                    <div className={styles.optionalNeedsGrid}>
                        <div className={styles.optionalIllustrationCard}>
                            <div className={styles.optionalIllustrationWrap}>
                                <EarlyIllustration className={styles.optionalIllustrationSvg} />
                            </div>
                            <div className={styles.optionalIllustrationTitle}>Early Care</div>
                            <div className={styles.optionalIllustrationSubtitle}>Initial assistance needs</div>
                            <div className={styles.optionalHoursPill}>
                                <span className={styles.optionalHoursValue}>{Math.round(initialHoursPerWeek)}</span>
                                <span className={styles.optionalHoursUnit}> hrs/week</span>
                            </div>
                        </div>

                        <div className={styles.optionalCenterDuration}>
                            <div className={styles.optionalCenterLabel}>Length of Care</div>
                            <div className={styles.optionalCenterValue}>
                                {formatDurationYears(ltcDurationYears)}
                            </div>
                            <div className={styles.optionalCenterUnit}>
                                years{isDurationCustom(phaseCosts) ? ' (edited)' : ''}
                            </div>
                            <div className={styles.optionalCenterLine} />
                        </div>

                        <div className={cn(styles.optionalIllustrationCard, styles.optionalLateCareCard)}>
                            <div className={styles.optionalIllustrationWrap}>
                                <LateIllustration className={styles.optionalIllustrationSvg} />
                            </div>
                            <div className={styles.optionalIllustrationTitle}>Later Care</div>
                            <div className={styles.optionalIllustrationSubtitle}>Intensive support needs</div>
                            <div className={styles.optionalHoursPill}>
                                <span className={styles.optionalHoursValue}>{Math.round(laterHoursPerWeek)}</span>
                                <span className={styles.optionalHoursUnit}> hrs/week</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
}

function PdfCostFactorsSection() {
        const client = useSelector(selectClient)!;
        const {
            clientFirstName,
            allPhaseCosts: {
                allPhaseTotalCost,
                allPhaseInflatedFamilyCareCost,
                allPhaseInflatedProfessionalShareCost,
            },
            intakeSurvey: { clientHasStartedLtc },
        } = client;

        const allPhaseInflatedCost =
            allPhaseInflatedProfessionalShareCost + allPhaseInflatedFamilyCareCost;
        const healthCareInflationContribution = allPhaseInflatedCost - allPhaseTotalCost;

        return (
            <div className={styles.optionalSectionContainer}>
                <div className={styles.optionalSectionHeader}>
                    <Coins size={16} />
                    {clientFirstName}&rsquo;s Estimated{clientHasStartedLtc ? ' Remaining' : ''} LTC Costs
                </div>
                <div className={styles.optionalSectionBody}>
                    <div className={styles.costFactorRow}>
                        <span className={styles.costFactorLabel}>
                            {clientHasStartedLtc ? 'Cost of Remaining Care' : 'Cost of Care'}
                        </span>
                        <span className={cn(styles.costFactorValue, styles.costFactorIncrease)}>
                            +{formatCurrency(allPhaseTotalCost)}
                        </span>
                    </div>
                    <div className={styles.costFactorRow}>
                        <span className={styles.costFactorLabel}>Healthcare Inflation</span>
                        <span className={cn(styles.costFactorValue, styles.costFactorIncrease)}>
                            +{formatCurrency(healthCareInflationContribution)}
                        </span>
                    </div>
                    <div className={styles.costFactorRow}>
                        <span className={styles.costFactorLabel}>Family Caregiving</span>
                        <span className={cn(styles.costFactorValue, styles.costFactorDecrease)}>
                            -{formatCurrency(allPhaseInflatedFamilyCareCost)}
                        </span>
                    </div>
                </div>
                <div className={styles.costFactorTotalRow}>
                    <div>
                        <div>Estimated Total Costs</div>
                        <div className={styles.costFactorSubtext}>*without a funding plan</div>
                    </div>
                    <span className={styles.costFactorTotalValue}>
                        {formatCurrency(allPhaseInflatedProfessionalShareCost)}
                    </span>
                </div>
            </div>
        );
}

function FundingDisclosure({
    clientFirstName,
    ltcDisplayRoundingStep = null,
}: {
    clientFirstName: string;
    ltcDisplayRoundingStep?: number | null;
}) {
    const ltcRoundingWord =
        ltcDisplayRoundingStep != null
            ? ltcRoundingDisclaimerWord(ltcDisplayRoundingStep)
            : null;

    return (
        <div className={styles.disclosureContainer}>
            <div className={styles.disclosureTitle}>Disclosures</div>
            <p className={styles.disclosureText}>
                {clientFirstName}'s projected LTC cost and age they will need LTC are predictions from
                models trained on other families' LTC journeys. These predictions are estimates and are not
                guaranteed. {clientFirstName}'s LTC costs may be higher or lower, and may begin sooner or
                later than what is shown here. There is no guarantee that a plan built on these numbers will
                cover {clientFirstName}'s costs.
            </p>
            <p className={styles.disclosureText}>
                The investing and insurance information provided on this page is for educational purposes
                only. Waterlily Caregiving, Inc. does not offer advisory or brokerage services, nor does it
                recommend or advise users to buy or sell particular stocks, securities insurance policies,
                or other financial products. Any information entered about an insurance policy is a subset
                of policy terms and can only be used to show an estimate of costs covered by a policy. Any
                policy will likely have terms that limit coverage beyond what is considered here.
            </p>
            {ltcRoundingWord != null && (
                <p className={styles.disclosureText}>
                    Where shown, projected long-term care dollar amounts in this report are rounded to the
                    nearest {ltcRoundingWord} dollars for readability and may not match unrounded model
                    outputs.
                </p>
            )}
            <p className={styles.disclosureText}>
                Additional disclaimers and disclosures on our models can be found on our site:{' '}
                <a
                    href="https://www.waterlily.com/planning-for-yourself/disclosures-disclaimers"
                    className={styles.disclosureLink}
                >
                    waterlily.com/planning-for-yourself/disclosures-disclaimers
                </a>
                .
            </p>
        </div>
    );
}

    function FundingRow({
        label,
        value,
        className,
    }: {
        label: ReactNode;
        value: ReactNode;
        className?: string;
    }) {
        return (
            <div className={styles.fundingRow}>
                <span className={styles.fundingRowLabel}>{label}</span>
                <span className={cn(styles.fundingRowValue, className)}>{value}</span>
            </div>
        );
    }

    function AppendixFraction({
        numerator,
        denominator,
    }: {
        numerator: ReactNode;
        denominator: ReactNode;
    }) {
        return (
            <div className={styles.appendixFraction}>
                <div className={styles.appendixFractionNumerator}>{numerator}</div>
                <div
                    className={styles.appendixFractionBar}
                    aria-hidden
                />
                <div className={styles.appendixFractionDenominator}>{denominator}</div>
            </div>
        );
    }

    function RoiAppendix({ roiDisplayType }: { roiDisplayType?: PdfRoiDisplayType }) {
        if (!roiDisplayType || roiDisplayType === 'hidden') return null;

        return (
            <div className={styles.appendixContainer}>
                <div className={styles.appendixHeader}>
                    <div className={styles.appendixHeaderTitle}>
                        {roiDisplayType === 'totalROI' && 'APPENDIX: SIMPLE ROI (RETURN ON INVESTMENT)'}
                        {roiDisplayType === 'CAGR' && 'APPENDIX: CAGR (COMPOUND ANNUAL GROWTH RATE)'}
                        {roiDisplayType === 'IRR' && 'APPENDIX: IRR (INTERNAL RATE OF RETURN)'}
                    </div>
                </div>
                <div className={styles.appendixBody}>
                    {roiDisplayType === 'totalROI' && (
                        <>
                            <p className={styles.disclosureText}>
                                Simple ROI compares the total projected payout to the total investment, without
                                considering the time over which payments are made.
                            </p>
                            <div className={styles.appendixEquation}>
                                <span className={styles.appendixEqLabel}>ROI =</span>
                                <AppendixFraction
                                    numerator="Total Payout − Total Investment"
                                    denominator="Total Investment"
                                />
                            </div>
                        </>
                    )}

                    {roiDisplayType === 'CAGR' && (
                        <>
                            <p className={styles.disclosureText}>
                                CAGR provides a &ldquo;smoothed out&rdquo; annualized rate of return, showing what the
                                annual growth rate would have been if it had occurred evenly over the entire period.
                                This is useful for comparing a policy&rsquo;s performance to other types of long-term
                                investments.
                            </p>
                            <div className={styles.appendixEquation}>
                                <span className={styles.appendixEqLabel}>CAGR =</span>
                                <span className={styles.appendixCagrGroup}>
                                    <span className={styles.appendixParen}>(</span>
                                    <AppendixFraction
                                        numerator="Total Payout"
                                        denominator="Present Value of Investment"
                                    />
                                    <span className={styles.appendixParen}>)</span>
                                    <sup className={styles.appendixExponent}>1/Years</sup>
                                </span>
                                <span className={styles.appendixEqTail}>− 1</span>
                            </div>
                            <p className={styles.disclosureText}>
                                The CAGR calculation includes a discount rate used to calculate the Present Value of
                                Investment. This rate is only relevant for policies with future premium payments. A
                                higher discount rate means future premiums are considered less costly in today&rsquo;s
                                dollars. For single-premium (one-time) investments, the discount rate has no impact.
                            </p>
                        </>
                    )}

                    {roiDisplayType === 'IRR' && (
                        <>
                            <p className={styles.disclosureText}>
                                IRR represents the annualized effective rate of return that makes the net present
                                value of all cash flows equal to zero. Unlike CAGR, IRR accounts for the exact timing
                                of all premium payments and benefit payouts throughout the policy lifetime, providing
                                a more precise measure of investment performance when cash flows occur at irregular
                                intervals.
                            </p>
                            <div className={styles.appendixEquation}>
                                <span
                                    className={styles.appendixSumGroup}
                                    aria-label="Sum from i equals 1 to n"
                                >
                                    <sup className={styles.appendixSumUpper}>n</sup>
                                    <span className={styles.appendixSumSymbol}>Σ</span>
                                    <sub className={styles.appendixSumLower}>i=1</sub>
                                </span>
                                <span className={styles.appendixIRRSummand}>
                                    CF<sub>i</sub> / (1 + IRR)
                                    <sup>
                                        days<sub>i</sub>/365
                                    </sup>
                                </span>
                                <span className={styles.appendixEqTail}>= 0</span>
                            </div>
                            <p className={styles.disclosureText}>
                                Where CF<sub>i</sub> is the cash flow at time i (negative for investments, positive
                                for returns), and days<sub>i</sub> is the number of days from the base year to cash
                                flow i. IRR is calculated using the XIRR method, which uses the actual dates of cash
                                flows rather than assuming annual periods.
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
